import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import './WebsitePublish.css';
import {
	EmptyState,
	FieldError,
	Link,
	LocaleCode,
	MainImageVariant,
	Spinner,
	UnprocessableError,
	useDeferredLoading,
} from '@anticatrade/shared';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import SlOption from '@shoelace-style/shoelace/dist/react/option/index.js';
import SlRadio from '@shoelace-style/shoelace/dist/react/radio/index.js';
import SlRadioGroup from '@shoelace-style/shoelace/dist/react/radio-group/index.js';
import SlSelect from '@shoelace-style/shoelace/dist/react/select/index.js';
import SlTextarea from '@shoelace-style/shoelace/dist/react/textarea/index.js';
import { AppContext, appContext } from '../../../../context/AppContext';
import { ErrorField } from '../../../base/ErrorField/ErrorField';
import { Heading } from '../../../base/Heading/Heading';
import { useWebsitePublish } from './useWebsitePublish';

const availableMainImageVariants = (
	hasOriginal: boolean,
	isCutout: boolean,
): MainImageVariant[] => {
	if (!hasOriginal) {
		return ['cutout'];
	}
	return isCutout
		? ['cutout', 'original-squared', 'original-natural']
		: ['original-squared', 'original-natural'];
};

const WebsitePublish = () => {
	const params = useParams();
	const productID = Number(params.productID);
	const merchantIntegrationID = Number(params.merchantIntegrationID);

	const [name, setName] = useState<string>('');
	const [longDescription, setLongDescription] = useState<string>('');
	const [price, setPrice] = useState<string>('');
	const [sku, setSku] = useState<string>('');
	const [width, setWidth] = useState<string>('');
	const [length, setLength] = useState<string>('');
	const [height, setHeight] = useState<string>('');
	const [categoryIDs, setCategoryIDs] = useState<number[]>([]);
	const [attributeIDs, setAttributeIDs] = useState<Record<number, number[]>>({});
	const [mainImageVariant, setMainImageVariant] = useState<MainImageVariant>('cutout');
	const [error, setError] = useState<FieldError | undefined>(undefined);
	const [isPublishing, setIsPublishing] = useState<boolean>(false);

	const { t, i18n } = useTranslation();
	const { showStatus, handleError, redirect } = useContext(
		appContext as React.Context<AppContext>,
	);
	const { isLoading, product, integration, taxonomy, publish } = useWebsitePublish(
		productID,
		merchantIntegrationID,
	);
	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	useEffect(() => {
		if (product == null) {
			return;
		}
		const texts = product.texts[i18n.language as LocaleCode];
		setName(texts?.title ?? '');
		setLongDescription(texts?.description ?? '');
		setPrice(String(product.price));
		setSku(product.sku ?? '');
		setWidth(product.width != null ? String(product.width) : '');
		setHeight(product.height != null ? String(product.height) : '');
		// WooCommerce has a single "length" for the third axis, while AT
		// splits it into length/depth/diameter (usually mutually
		// exclusive). Fall back through them so whichever the merchant
		// filled ends up in WooCommerce.
		const third = product.length ?? product.depth ?? product.diameter;
		setLength(third != null ? String(third) : '');
	}, [product]);

	useEffect(() => {
		if (product == null || integration == null) {
			return;
		}
		// Seed the main image variant from the integration default.
		const available = availableMainImageVariants(
			product.originalImage != null,
			product.image.isCutout === true,
		);
		const preferred = integration.settings.mainImageVariant ?? 'cutout';
		setMainImageVariant(available.includes(preferred) ? preferred : available[0]);
	}, [product, integration]);

	const onChangeAttribute = (attributeID: number, value: string[]) => {
		setAttributeIDs((selected) => ({
			...selected,
			[attributeID]: value.map((v) => Number(v)),
		}));
	};

	const onPublish = async () => {
		setError(undefined);

		if (name.trim() === '') {
			setError(new FieldError(t('Name is required'), 'name'));
			return;
		}
		const priceNumber = Number(price);
		if (price.trim() === '' || Number.isNaN(priceNumber) || priceNumber <= 0) {
			setError(new FieldError(t('Price is required'), 'price'));
			return;
		}

		const websiteProduct: Record<string, unknown> = {
			name: name.trim(),
			regular_price: price.trim(),
			description: longDescription.trim(),
			categories: categoryIDs.map((id) => ({ id })),
		};
		if (sku.trim() !== '') {
			websiteProduct.sku = sku.trim();
		}
		if (width.trim() !== '' || length.trim() !== '' || height.trim() !== '') {
			websiteProduct.dimensions = {
				width: width.trim(),
				length: length.trim(),
				height: height.trim(),
			};
		}
		const attributes = [];
		for (const id of Object.keys(attributeIDs)) {
			const attributeID = Number(id);
			const values = attributeIDs[attributeID];
			if (values.length === 0) {
				continue;
			}
			attributes.push({
				id: attributeID,
				options: values.map((valueID) => taxonomy!.attributes[attributeID].values[valueID]),
				visible: true,
			});
		}
		if (attributes.length > 0) {
			websiteProduct.attributes = attributes;
		}

		setIsPublishing(true);
		try {
			await publish(websiteProduct, mainImageVariant);
		} catch (err) {
			setIsPublishing(false);
			if (err instanceof UnprocessableError && err.code === 'IntegrationUnauthorized') {
				showStatus({
					variant: 'warning',
					icon: 'exclamation-triangle',
					text: t(
						'The website refused the credentials. Check the consumer key and secret.',
					),
				});
				return;
			}
			handleError(err as Error);
			return;
		}
		setIsPublishing(false);
		showStatus({
			variant: 'success',
			icon: 'check-circle',
			text: t(
				'The website could take several minutes to process the item images and publish it',
			),
		});
		redirect('products');
	};

	if (showContent && (product == null || integration == null || taxonomy == null)) {
		return (
			<div className='website-publish'>
				<EmptyState
					icon='plug'
					title={t('Integration not found')}
					description={t('This integration is no longer available')}
				>
					<Link path='/products'>
						<SlButton>{t('Back')}</SlButton>
					</Link>
				</EmptyState>
			</div>
		);
	}

	const imageBaseURL = `${import.meta.env.VITE_PRODUCT_IMAGE_URL}/${productID}`;
	const originalFileName = product?.originalImage?.fileName;
	const mainImagePreviewURL =
		mainImageVariant === 'original-natural' && originalFileName != null
			? `${imageBaseURL}/large-${originalFileName}.webp`
			: mainImageVariant === 'original-squared' && originalFileName != null
				? `${imageBaseURL}/square-large-${originalFileName}.webp`
				: product?.image != null && product.image.fileName !== ''
					? `${imageBaseURL}/thumbnail-${product.image.fileName}.webp`
					: '';

	const availableVariants =
		product != null
			? availableMainImageVariants(
					product.originalImage != null,
					product.image.isCutout === true,
				)
			: [];

	const canChooseMainImageVariant = availableVariants.length > 1;
	const mainImageVariantOptions: { value: MainImageVariant; label: string; hint: string }[] = [
		{
			value: 'cutout',
			label: t('Cutout'),
			hint: t('The item on its own, clean and uniform — like a product catalog.'),
		},
		{
			value: 'original-squared',
			label: t('Original photo, squared'),
			hint: t('Your original photo, background included, fitted into a square.'),
		},
		{
			value: 'original-natural',
			label: t('Original photo, full proportions'),
			hint: t('Your original photo exactly as you framed it, in its own proportions.'),
		},
	];

	return (
		<div className='website-publish'>
			{showContent && product != null && integration != null && taxonomy != null ? (
				<>
					<Heading
						left={
							<div className='website-publish__heading'>
								<img
									className='website-publish__logo'
									src={integration.metadata?.logoURL}
									alt={integration.metadata?.title}
								/>
								<h1 className='website-publish__title'>
									{t('Publish on')} {integration.metadata?.title}
								</h1>
							</div>
						}
					/>

					<div className='website-publish__form wrapped'>
						<ErrorField name='name' error={error}>
							<SlInput
								label={t('Name')}
								size='medium'
								value={name}
								onSlInput={(e: any) => setName(e.target.value)}
								required
							/>
						</ErrorField>

						<SlTextarea
							label={t('Description')}
							size='medium'
							rows={6}
							value={longDescription}
							onSlInput={(e: any) => setLongDescription(e.target.value)}
						/>

						<div className='website-publish__row'>
							<ErrorField name='price' error={error}>
								<SlInput
									label={t('Price')}
									size='medium'
									type='number'
									value={price}
									onSlInput={(e: any) => setPrice(e.target.value)}
									required
								>
									<div slot='suffix'>€</div>
								</SlInput>
							</ErrorField>
							<SlInput
								label={t('Sku')}
								size='medium'
								value={sku}
								onSlInput={(e: any) => setSku(e.target.value)}
							/>
						</div>

						<div className='website-publish__row'>
							<SlInput
								label={`${t('Width')} (cm)`}
								size='medium'
								type='number'
								value={width}
								onSlInput={(e: any) => setWidth(e.target.value)}
							/>
							<SlInput
								label={`${t('Length')} (cm)`}
								size='medium'
								type='number'
								value={length}
								onSlInput={(e: any) => setLength(e.target.value)}
							/>
							<SlInput
								label={`${t('Height')} (cm)`}
								size='medium'
								type='number'
								value={height}
								onSlInput={(e: any) => setHeight(e.target.value)}
							/>
						</div>

						<SlSelect
							label={t('Categories')}
							size='medium'
							multiple
							clearable
							value={categoryIDs.map((id) => String(id))}
							onSlChange={(e: any) =>
								setCategoryIDs((e.target.value as string[]).map((v) => Number(v)))
							}
						>
							{Object.entries(taxonomy.categories).map(([id, categoryName]) => (
								<SlOption key={id} value={id}>
									{categoryName}
								</SlOption>
							))}
						</SlSelect>

						{Object.entries(taxonomy.attributes).map(([id, attribute]) => {
							const attributeID = Number(id);
							const selected = attributeIDs[attributeID];
							return (
								<SlSelect
									key={id}
									label={attribute.name}
									size='medium'
									multiple
									clearable
									value={(selected ?? []).map((v) => String(v))}
									onSlChange={(e: any) =>
										onChangeAttribute(attributeID, e.target.value)
									}
								>
									{Object.entries(attribute.values).map(
										([valueID, valueName]) => (
											<SlOption key={valueID} value={valueID}>
												{valueName}
											</SlOption>
										),
									)}
								</SlSelect>
							);
						})}

						<div className='website-publish__images'>
							<div className='website-publish__images-label'>{t('Images')}</div>
							<div className='website-publish__images-list'>
								{mainImagePreviewURL !== '' && (
									<img
										className='website-publish__image'
										src={mainImagePreviewURL}
										alt=''
									/>
								)}
								{product.gallery.map((g) =>
									g.fileName !== '' ? (
										<img
											className='website-publish__image'
											key={g.id}
											src={`${imageBaseURL}/thumbnail-${g.fileName}.webp`}
											alt=''
										/>
									) : null,
								)}
							</div>
							{canChooseMainImageVariant && (
								<SlRadioGroup
									className='website-publish__main-variant'
									label={t('Main photo')}
									value={mainImageVariant}
									onSlChange={(e: any) =>
										setMainImageVariant(e.target.value as MainImageVariant)
									}
								>
									{mainImageVariantOptions
										.filter((option) =>
											availableVariants.includes(option.value),
										)
										.map((option) => (
											<SlRadio key={option.value} value={option.value}>
												{option.label}
												<span className='website-publish__option-hint'>
													{option.hint}
												</span>
											</SlRadio>
										))}
								</SlRadioGroup>
							)}
						</div>
					</div>

					<div className='website-publish__actions'>
						<Link path='/products'>
							<SlButton>{t('Cancel')}</SlButton>
						</Link>
						<SlButton variant='primary' loading={isPublishing} onClick={onPublish}>
							{t('Publish')}
						</SlButton>
					</div>
				</>
			) : showSpinner ? (
				<div className='website-publish__loading'>
					<Spinner />
					<div className='website-publish__loading-text'>
						{t('We are fetching the categories and attributes of your website')}
					</div>
				</div>
			) : null}
		</div>
	);
};

export { WebsitePublish };
